// HRMS_STADO/src/views/hrms/offices/AddOffice.jsx
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createOffice } from "services/hrms.api";
import { LocateFixed, MapPin, ChevronLeft, Plus, Trash2, Clock } from "lucide-react";

// 📦 country / state / city dropdown library
import { CountrySelect, StateSelect, CitySelect } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";

const defaultShift = () => ({
  name: "General",
  startTime: "09:00",
  endTime: "18:00",
  graceMins: 0,        // ← add
});

const emptyShift = () => ({
  name: "",
  startTime: "",
  endTime: "",
  graceMins: 0,        // ← add
});

function isHHMM(t) {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(String(t || ""));
}

export default function AddOffice() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    country: "",
    lat: "",
    lng: "",
    radiusMeters: "",
    phone: "",
    workStart: "",
    workEnd: "",
    description: "",
    facilities: "", // comma-separated (simpler)
    imageData: "", // base64 data URL
  });

  // ✅ NEW: shifts created along with office
  const [shifts, setShifts] = useState([defaultShift()]);

  // internal ids needed by the library
  const [countryId, setCountryId] = useState(0);
  const [stateId, setStateId] = useState(0);

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const latNum = Number(form.lat);
  const lngNum = Number(form.lng);
  const hasCoords =
    Number.isFinite(latNum) &&
    Number.isFinite(lngNum) &&
    form.lat !== "" &&
    form.lng !== "";

  const mapSrc = useMemo(() => {
    if (!hasCoords) return "";
    return `https://www.openstreetmap.org/export/embed.html?bbox=${lngNum - 0.01
      }%2C${latNum - 0.01}%2C${lngNum + 0.01}%2C${latNum + 0.01}&layer=mapnik&marker=${latNum}%2C${lngNum}`;
  }, [hasCoords, latNum, lngNum]);

  const onUseCurrentLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported.");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((p) => ({
          ...p,
          lat: String(latitude),
          lng: String(longitude),
        }));
      },
      () => alert("Unable to fetch current location."),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const onPickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return alert("Please select an image file.");
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, imageData: reader.result }));
    reader.readAsDataURL(file);
  };

  // ✅ shift handlers
  const addShift = () => setShifts((prev) => [...prev, emptyShift()]);
  const removeShift = (idx) => {
    setShifts((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateShift = (idx, key, value) => {
    setShifts((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)));
  };

  const validateShifts = () => {
    if (!Array.isArray(shifts) || shifts.length === 0) return "Please add at least one shift";

    for (let i = 0; i < shifts.length; i++) {
      const s = shifts[i];
      const name = String(s.name || "").trim();
      const startTime = String(s.startTime || "").trim();
      const endTime = String(s.endTime || "").trim();
      const grace = Number(s.graceMins ?? 0);

      if (!name) return `Shift ${i + 1}: Please enter shift name`;
      if (!isHHMM(startTime)) return `Shift ${i + 1}: Please select valid start time`;
      if (!isHHMM(endTime)) return `Shift ${i + 1}: Please select valid end time`;
      if (!Number.isInteger(grace) || grace < 0 || grace > 120)
        return `Shift ${i + 1}: Grace minutes must be between 0 and 120`;
    }

    // Optional: prevent exact duplicates by name within same office
    const names = shifts.map((s) => String(s.name || "").trim().toLowerCase());
    const dup = names.find((n, idx) => n && names.indexOf(n) !== idx);
    if (dup) return "Shift names must be unique";

    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) return alert("Please enter the office name");
    if (!form.address.trim()) return alert("Please enter the address");
    if (!form.city.trim()) return alert("Please select the city");
    if (!form.state.trim()) return alert("Please select the state");
    if (!form.country.trim()) return alert("Please select the country");
    if (!hasCoords) return alert("Please provide valid latitude and longitude");

    // ✅ validate shifts
    const shiftErr = validateShifts();
    if (shiftErr) return alert(shiftErr);

    const payload = {
      ...form,
      lat: Number(form.lat),
      lng: Number(form.lng),
      radiusMeters: Number(form.radiusMeters || 50),
      facilities: form.facilities
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),

      // ✅ important: send shifts to backend
      shifts: shifts.map((s) => ({
        name: String(s.name).trim(),
        startTime: s.startTime,
        endTime: s.endTime,
        graceMins: Number(s.graceMins ?? 0),   // ← add
      })),
    };

    await createOffice(payload);
    navigate(-1); // back to list
  };

  return (
    <div className="mx-auto space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
            aria-label="Back"
            type="button"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <h2 className="text-2xl font-semibold dark:text-white">Add Office</h2>
        </div>
      </div>

      {/* Card */}
      <form
        onSubmit={onSubmit}
        className="rounded-2xl border border-gray-200 p-4 dark:border-white/10"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Name">
            <input
              name="name"
              value={form.name}
              onChange={set}
              placeholder="e.g., Head Office"
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              required
            />
          </Field>

          {/* Country */}
          <Field label="Country">
            <CountrySelect
              placeHolder="Select Country"
              onChange={(val) => {
                setCountryId(val.id);
                setStateId(0);
                setForm((p) => ({
                  ...p,
                  country: val.name || "",
                  state: "",
                  city: "",
                }));
              }}
              containerClassName="w-full"
              inputClassName="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          {/* State */}
          <Field label="State">
            <StateSelect
              countryid={countryId}
              placeHolder="Select State"
              onChange={(val) => {
                setStateId(val.id);
                setForm((p) => ({
                  ...p,
                  state: val.name || "",
                  city: "",
                }));
              }}
              containerClassName="w-full"
              inputClassName="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          {/* City */}
          <Field label="City">
            <CitySelect
              countryid={countryId}
              stateid={stateId}
              placeHolder="Select City"
              onChange={(val) => {
                setForm((p) => ({ ...p, city: val.name || "" }));
              }}
              containerClassName="w-full"
              inputClassName="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <Field label="Address" full>
            <input
              name="address"
              value={form.address}
              onChange={set}
              placeholder="Street, area, landmark"
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              required
            />
          </Field>

          <Field label="Phone (optional)">
            <input
              name="phone"
              value={form.phone}
              onChange={set}
              placeholder="+91-XXXXXXXXXX"
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <Field label="Working In (Start)">
            <input
              type="time"
              name="workStart"
              value={form.workStart}
              onChange={set}
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <Field label="Working Out (End)">
            <input
              type="time"
              name="workEnd"
              value={form.workEnd}
              onChange={set}
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <Field label="Radius (meters)">
            <input
              name="radiusMeters"
              value={form.radiusMeters}
              onChange={set}
              placeholder="e.g., 200"
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
            />
          </Field>

          <Field label="Latitude">
            <div className="flex items-center gap-2">
              <input
                name="lat"
                value={form.lat}
                onChange={set}
                placeholder="e.g., 25.6093"
                className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                required
              />
              <button
                type="button"
                onClick={onUseCurrentLocation}
                className="inline-flex items-center gap-1 rounded-lg border px-2.5 py-2 text-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                title="Use current location"
              >
                <LocateFixed className="h-4 w-4 dark:text-white" /> Use current
              </button>
            </div>
          </Field>

          <Field label="Longitude">
            <input
              name="lng"
              value={form.lng}
              onChange={set}
              placeholder="e.g., 85.1234"
              className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              required
            />
          </Field>

          {/* Map + Link */}
          <div className="grid gap-3 md:col-span-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <MapPin className="h-4 w-4" />
              {hasCoords ? (
                <>
                  <span>
                    Previewing: {latNum.toFixed(6)}, {lngNum.toFixed(6)}
                  </span>
                  <a
                    href={`https://www.google.com/maps?q=${form.lat},${form.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 rounded px-2 py-0.5 font-medium text-brand-600 hover:underline"
                  >
                    Open in Google Maps
                  </a>
                </>
              ) : (
                <span>Enter latitude & longitude to preview</span>
              )}
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
              {hasCoords ? (
                <iframe
                  title="office-map"
                  src={mapSrc}
                  className="h-56 w-full"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-56 items-center justify-center text-sm text-gray-400">
                  Map preview
                </div>
              )}
            </div>
          </div>

          {/* ✅ NEW: Shifts Section */}
          <div className="md:col-span-2">
            <div className="rounded-xl border border-gray-200 p-3 dark:border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-600 dark:text-white/70" />
                  <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
                    Shifts (Required)
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={addShift}
                  className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                >
                  <Plus className="h-4 w-4" /> Add Shift
                </button>
              </div>

              <div className="mt-3 space-y-3">
                {shifts.map((s, idx) => (
                  <div
                    key={idx}
                    className="grid gap-2 rounded-lg border border-gray-200 p-3 md:grid-cols-13 dark:border-white/10"
                  >
                    {/* Shift Name — col-span-4 */}
                    <div className="md:col-span-4">
                      <label className="mb-1 block text-xs text-gray-600 dark:text-white/70">
                        Shift Name
                      </label>
                      <input
                        value={s.name}
                        onChange={(e) => updateShift(idx, "name", e.target.value)}
                        placeholder="Morning / Evening / Night"
                        className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                      />
                    </div>

                    {/* Start Time — col-span-2 */}
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs text-gray-600 dark:text-white/70">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={s.startTime}
                        onChange={(e) => updateShift(idx, "startTime", e.target.value)}
                        className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                      />
                    </div>

                    {/* End Time — col-span-2 */}
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs text-gray-600 dark:text-white/70">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={s.endTime}
                        onChange={(e) => updateShift(idx, "endTime", e.target.value)}
                        className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                      />
                    </div>

                    {/* Grace Mins — col-span-3 */}
                    <div className="md:col-span-3">
                      <label className="mb-1 block text-xs text-gray-600 dark:text-white/70">
                        Grace (mins)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={120}
                        value={s.graceMins ?? 0}
                        onChange={(e) => updateShift(idx, "graceMins", Math.min(120, Math.max(0, Number(e.target.value) || 0)))}
                        placeholder="0"
                        className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
                      />
                      <p className="mt-0.5 text-xs text-gray-400">0 = no grace</p>
                    </div>

                    {/* Remove — col-span-2 */}
                    <div className="md:col-span-2 flex items-end justify-end">
                      <button
                        type="button"
                        onClick={() => removeShift(idx)}
                        disabled={shifts.length === 1}
                        className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                        title={shifts.length === 1 ? "At least one shift is required" : "Remove shift"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <p className="mt-2 text-xs text-gray-500">
                Tip: Overnight shift is allowed (e.g., 22:00 to 06:00).
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <Field label="Description">
              <textarea
                name="description"
                rows={3}
                value={form.description}
                onChange={set}
                placeholder="Describe this office (parking, canteen, entry policy, etc.)"
                className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
            </Field>
          </div>

          {/* Facilities (comma-separated) */}
          <div className="md:col-span-2">
            <Field label="Facilities (comma-separated)">
              <input
                name="facilities"
                value={form.facilities}
                onChange={set}
                placeholder="Parking, Canteen, Meeting Rooms"
                className="dark:bg-transparent w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-navy-800 dark:text-white"
              />
            </Field>
          </div>

          {/* Image */}
          <div className="md:col-span-2">
            <Field label="Office Image">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => onPickImage(e.target.files?.[0])}
                  className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-xs hover:file:bg-gray-50 dark:file:border-white/10 dark:file:bg-white/5"
                />
                {form.imageData ? (
                  <img
                    src={form.imageData}
                    alt="Preview"
                    className="h-16 w-16 rounded-lg object-cover ring-1 ring-gray-200 dark:text-white dark:ring-white/10"
                  />
                ) : null}
              </div>
              <p className="mt-1 text-xs text-gray-500">PNG/JPG, up to ~2MB recommended.</p>
            </Field>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button className="rounded-lg bg-gradient-to-r from-[#ef145d] to-[#e3955e] px-5 py-2 text-sm text-white hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-brand-500">
            Save Office
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-white/80">
        {label}
      </label>
      {children}
    </div>
  );
}